'use client'

import React, { RefObject, useCallback, useEffect, useState } from 'react'

export interface UseFloatingPiPControlsParams {
  videoRef: RefObject<HTMLVideoElement | null>
  isOpen: boolean
  videoUrl: string | null
  currentTime: number
  isMuted: boolean
  onClose: () => void
  onReopen?: (currentTime: number) => void
}

export interface UseFloatingPiPControlsReturn {
  isClosing: boolean
  isPaused: boolean
  isHovered: boolean
  muted: boolean
  isEnded: boolean
  setIsHovered: (value: boolean) => void
  handleClose: (e?: React.MouseEvent) => void
  handleVideoEnd: () => void
  handleTogglePlayPause: (e: React.MouseEvent) => void
  handleToggleMute: (e: React.MouseEvent) => void
  handleReload: (e: React.MouseEvent) => void
  handlePiPClick: () => void
}

export function useFloatingPiPControls({
  videoRef,
  isOpen,
  videoUrl,
  currentTime,
  isMuted,
  onClose,
  onReopen,
}: UseFloatingPiPControlsParams): UseFloatingPiPControlsReturn {
  const [isClosing, setIsClosing] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [muted, setMuted] = useState(isMuted)
  const [isEnded, setIsEnded] = useState(false)

  // Set current time and play when opened
  useEffect(() => {
    if (isOpen && videoRef.current && videoUrl) {
      videoRef.current.currentTime = currentTime
      videoRef.current.play().catch(console.error)
      setIsPaused(false)
      setIsEnded(false)
    }
  }, [isOpen, videoUrl, currentTime, videoRef])

  // Sync muted state to video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted
    }
  }, [muted, videoRef])

  // Handle close with animation
  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      setIsClosing(true)
      setTimeout(() => {
        setIsClosing(false)
        onClose()
      }, 300)
    },
    [onClose],
  )

  // Handle video end
  const handleVideoEnd = () => {
    setIsEnded(true)
  }

  // Toggle play/pause
  const handleTogglePlayPause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!videoRef.current) return
    if (videoRef.current.paused) {
      videoRef.current.play().catch(console.error)
      setIsPaused(false)
    } else {
      videoRef.current.pause()
      setIsPaused(true)
    }
  }, [videoRef])

  // Toggle mute
  const handleToggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setMuted((prev) => !prev)
  }, [])

  // Handle reload (replay from beginning)
  const handleReload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!videoRef.current) return
    setIsEnded(false)
    videoRef.current.currentTime = 0
    videoRef.current.play().catch(console.error)
    setIsPaused(false)
  }, [videoRef])

  // Click on PiP to reopen modal
  const handlePiPClick = useCallback(() => {
    if (!onReopen || !videoRef.current) return
    const time = videoRef.current.currentTime
    videoRef.current.pause()
    onReopen(time)
  }, [onReopen, videoRef])

  return {
    isClosing,
    isPaused,
    isHovered,
    muted,
    isEnded,
    setIsHovered,
    handleClose,
    handleVideoEnd,
    handleTogglePlayPause,
    handleToggleMute,
    handleReload,
    handlePiPClick,
  }
}
